<template>
  <section class="assignment-panel">
    <header class="assignment-panel__header">
      <div>
        <p class="assignment-panel__eyebrow">{{ phaseLabelText }}</p>
        <h4 class="assignment-panel__title">{{ label('judge.assignment.title', 'Asignacion de rondas') }}</h4>
        <p class="assignment-panel__subtitle">
          {{ label('judge.assignment.subtitle', 'Controla la cola operativa y reasigna rondas sin romper el flujo actual.') }}
        </p>
      </div>
      <div class="assignment-panel__stats">
        <article class="assignment-stat-card">
          <span class="assignment-stat-card__label">{{ label('judge.assignment.visible_rounds', 'Rondas visibles') }}</span>
          <strong class="assignment-stat-card__value">{{ rounds.length }}</strong>
        </article>
        <article class="assignment-stat-card">
          <span class="assignment-stat-card__label">{{ label('judge.assignment.waiting_judges', 'En espera') }}</span>
          <strong class="assignment-stat-card__value">{{ waitingQueueJudges.length }}</strong>
        </article>
        <article class="assignment-stat-card">
          <span class="assignment-stat-card__label">{{ label('judge.assignment.unassigned', 'Sin juez') }}</span>
          <strong class="assignment-stat-card__value">{{ unassignedVisibleRounds }}</strong>
        </article>
        <article class="assignment-stat-card">
          <span class="assignment-stat-card__label">{{ label('judge.assignment.preview_changes', 'Cambios previstos') }}</span>
          <strong class="assignment-stat-card__value">{{ autoAssignmentsToPersist.length }}</strong>
        </article>
      </div>
    </header>

    <div class="assignment-panel__body">
      <aside class="assignment-panel__sidebar">
        <section class="assignment-card">
          <div class="assignment-card__header">
            <div>
              <h5>{{ label('judge.assignment.mode_title', 'Modo de asignacion') }}</h5>
              <p>{{ modeHintText }}</p>
            </div>
          </div>

          <div class="assignment-mode-toggle" role="group" aria-label="Assignment mode">
            <button
              type="button"
              class="btn btn-sm"
              :class="assignmentMode === 'automatic' ? 'btn-primary' : 'btn-outline-primary'"
              @click="assignmentMode = 'automatic'"
            >
              {{ label('judge.assignment.mode_automatic', 'Automatico') }}
            </button>
            <button
              type="button"
              class="btn btn-sm"
              :class="assignmentMode === 'manual' ? 'btn-primary' : 'btn-outline-primary'"
              @click="assignmentMode = 'manual'"
            >
              {{ label('judge.assignment.mode_manual', 'Manual') }}
            </button>
          </div>

          <div class="assignment-control-grid">
            <label class="assignment-field">
              <span>{{ label('judge.assignment.strategy', 'Estrategia') }}</span>
              <input type="text" class="form-control form-control-sm" value="Round robin" disabled>
            </label>

            <label class="assignment-field">
              <span>{{ label('judge.assignment.tech_release', 'Entrada del tecnico') }}</span>
              <select v-model="operatorReleaseMode" class="form-select form-select-sm">
                <option value="after_uploads">{{ label('judge.assignment.tech_release_after_uploads', 'Tras el ultimo upload') }}</option>
                <option value="always">{{ label('judge.assignment.tech_release_always', 'Siempre elegible') }}</option>
              </select>
            </label>
          </div>

          <div class="assignment-preview-summary" :class="{ 'assignment-preview-summary--warning': hasPreviewConflicts }">
            <strong>{{ previewSummaryTitle }}</strong>
            <p>{{ previewSummaryText }}</p>
          </div>

          <div v-if="assignmentMode === 'automatic' && automaticExecutionEnabled" class="assignment-actions assignment-actions--stacked">
            <button
              class="btn btn-sm btn-primary"
              :disabled="!hasPreviewChanges || hasPreviewConflicts"
              @click="emitApplyAutoAssignment"
            >
              <i class="fa-solid fa-wand-magic-sparkles"></i>
              <span>{{ label('judge.assignment.apply_preview', 'Aplicar propuesta') }}</span>
            </button>
          </div>
        </section>

        <section class="assignment-card">
          <div class="assignment-card__header">
            <div>
              <h5>{{ label('judge.assignment.call_queue', 'Cola de espera') }}</h5>
              <p>{{ queueHintText }}</p>
            </div>
          </div>

          <div v-if="timedPendingRounds.length" class="assignment-countdown-list">
            <article
              v-for="item in timedPendingRounds"
              :key="`countdown-${item.roundId}`"
              class="assignment-countdown-item"
            >
              <div class="assignment-countdown-item__main">
                <strong>{{ item.judgeLabel }}</strong>
                <p>{{ item.roundLabel }}</p>
                <small>{{ label('judge.assignment.timeout_hint', 'Si no cambia a scoring, se mueve al siguiente juez online.') }}</small>
              </div>
              <span class="assignment-chip assignment-chip--timer">{{ item.remainingText }}</span>
            </article>
          </div>

          <div v-if="callQueueJudges.length" class="assignment-callcenter-list">
            <article
              v-for="(judge, index) in callQueueJudges"
              :key="`queue-${judge.id}`"
              class="assignment-callcenter-item"
            >
              <div class="assignment-callcenter-item__position">
                {{ index + 1 }}
              </div>
              <div class="assignment-callcenter-item__main">
                <strong>{{ judge.label }}</strong>
                <p>{{ judge.queueDescription }}</p>
              </div>
              <span class="assignment-chip" :class="judge.queueChipClass">
                {{ judge.queueChipLabel }}
              </span>
            </article>
          </div>
          <p v-else class="assignment-empty">
            {{ label('judge.assignment.no_waiting_judges', 'No hay jueces elegibles en espera ahora mismo.') }}
          </p>
        </section>

        <section class="assignment-card">
          <div class="assignment-card__header">
            <div>
              <h5>{{ label('judge.assignment.judge_states', 'Estados de jueces') }}</h5>
              <p>{{ label('judge.assignment.judge_states_hint', 'Cada juez se clasifica por disponibilidad real para recibir la siguiente ronda.') }}</p>
            </div>
          </div>

          <div class="assignment-status-groups">
            <div v-if="technicalHeldJudges.length" class="assignment-status-group">
              <span class="assignment-status-group__title">{{ label('judge.assignment.technical_held', 'Tecnico retenido') }}</span>
              <article v-for="judge in technicalHeldJudges" :key="`tech-${judge.id}`" class="assignment-status-item">
                <strong>{{ judge.label }}</strong>
                <p>{{ judge.reason }}</p>
              </article>
            </div>

            <div v-if="occupiedJudges.length" class="assignment-status-group">
              <span class="assignment-status-group__title">{{ label('judge.assignment.occupied', 'Ocupados') }}</span>
              <article v-for="judge in occupiedJudges" :key="`busy-${judge.id}`" class="assignment-status-item">
                <strong>{{ judge.label }}</strong>
                <p>{{ judge.reason }}</p>
              </article>
            </div>

            <div v-if="offlineJudges.length" class="assignment-status-group">
              <span class="assignment-status-group__title">{{ label('judge.assignment.offline', 'Offline') }}</span>
              <article v-for="judge in offlineJudges" :key="`offline-${judge.id}`" class="assignment-status-item">
                <strong>{{ judge.label }}</strong>
                <p>{{ judge.reason }}</p>
              </article>
            </div>
          </div>
        </section>

        <section class="assignment-card">
          <div class="assignment-card__header">
            <h5>{{ label('judge.assignment.upcoming', 'Proximas rondas') }}</h5>
          </div>
          <div v-if="upcomingRounds.length" class="assignment-queue">
            <article
              v-for="round in upcomingRounds"
              :key="`queue-${round.id}`"
              class="assignment-queue__item"
            >
              <div>
                <strong>{{ formatRoundLabel(round) }}</strong>
                <p>{{ formatRoundMeta(round) }}</p>
              </div>
              <span class="assignment-chip" :class="{ 'assignment-chip--empty': !resolveCurrentJudgeLabel(round) }">
                {{ resolveCurrentJudgeLabel(round) || label('judge.assignment.unassigned_short', 'Sin juez') }}
              </span>
            </article>
          </div>
          <p v-else class="assignment-empty">
            {{ label('judge.assignment.no_upcoming', 'No hay rondas pendientes para anticipar.') }}
          </p>
        </section>
      </aside>

      <section class="assignment-card assignment-card--main">
        <div class="assignment-card__header assignment-card__header--main">
          <div>
            <h5>{{ label('judge.assignment.phase_rounds', 'Rondas de la fase seleccionada') }}</h5>
            <p>{{ phaseHintText }}</p>
          </div>
        </div>

        <div v-if="assignmentMode === 'automatic' && rounds.length" class="assignment-preview-banner" :class="{ 'assignment-preview-banner--warning': hasPreviewConflicts }">
          <strong>{{ previewSummaryTitle }}</strong>
          <p>{{ previewSummaryText }}</p>
        </div>

        <div v-if="!rounds.length" class="assignment-empty assignment-empty--large">
          {{ label('judge.assignment.no_rounds_visible', 'No hay rondas visibles en la fase actual.') }}
        </div>

        <div v-else class="assignment-table-wrap">
          <table class="assignment-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{{ label('judge.assignment.round', 'Ronda') }}</th>
                <th>{{ label('judge.assignment.current_judge', 'Juez actual') }}</th>
                <th>{{ label('judge.assignment.assign_to', 'Asignar a') }}</th>
                <th>{{ label('judge.assignment.preview', 'Propuesta') }}</th>
                <th>{{ label('judge.assignment.actions', 'Acciones') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(round, index) in rounds"
                :key="round.id"
                :class="{ 'assignment-row--affected': Boolean(previewAssignmentsByRoundId[String(round.id)]?.affectsPersistence) }"
              >
                <td>{{ index + 1 }}</td>
                <td>
                  <div class="assignment-round-cell">
                    <strong>{{ formatRoundLabel(round) }}</strong>
                    <p>{{ formatRoundMeta(round) }}</p>
                  </div>
                </td>
                <td>
                  <span class="assignment-chip" :class="{ 'assignment-chip--empty': !resolveCurrentJudgeLabel(round) }">
                    {{ resolveCurrentJudgeLabel(round) || label('judge.assignment.unassigned_short', 'Sin juez') }}
                  </span>
                </td>
                <td>
                  <select
                    class="form-select form-select-sm"
                    :value="draftAssignments[String(round.id)]"
                    :disabled="isRoundSaving(round.id) || isRoundAssignmentLocked(round) || !judges.length"
                    @change="updateDraft(round.id, $event.target.value)"
                  >
                    <option value="">{{ label('judge.assignment.unassigned_option', 'Sin asignar') }}</option>
                    <option
                      v-for="judge in judges"
                      :key="`judge-option-${round.id}-${judge.id}`"
                      :value="String(judge.id)"
                    >
                      {{ judge.label }}
                    </option>
                  </select>
                </td>
                <td>
                  <div v-if="isRoundAssignmentLocked(round)" class="assignment-preview-cell">
                    <span class="assignment-chip assignment-chip--busy">{{ label('judge.assignment.already_in_progress', 'Ya asignada') }}</span>
                    <p>{{ lockedRoundAssignmentHint(round) }}</p>
                  </div>
                  <div v-else-if="assignmentMode === 'automatic'" class="assignment-preview-cell">
                    <span
                      class="assignment-chip"
                      :class="previewChipClass(previewAssignmentsByRoundId[String(round.id)])"
                    >
                      {{ previewLabel(previewAssignmentsByRoundId[String(round.id)]) }}
                    </span>
                    <p>{{ previewDescription(previewAssignmentsByRoundId[String(round.id)]) }}</p>
                  </div>
                  <div v-else class="assignment-preview-cell">
                    <span class="assignment-chip assignment-chip--manual">{{ label('judge.assignment.manual_mode', 'Manual') }}</span>
                    <p>{{ label('judge.assignment.manual_mode_hint', 'La propuesta automatica queda desactivada mientras trabajas ronda a ronda.') }}</p>
                  </div>
                </td>
                <td>
                  <div class="assignment-actions">
                    <button
                      class="btn btn-sm btn-primary"
                      :disabled="isRoundAssignmentLocked(round) || !hasDraftChange(round) || isRoundSaving(round.id)"
                      @click="emitAssign(round)"
                    >
                      <i class="fa-solid fa-floppy-disk"></i>
                      <span>{{ isRoundSaving(round.id) ? label('common.saving', 'Guardando') : label('common.save', 'Guardar') }}</span>
                    </button>
                    <button
                      class="btn btn-sm btn-outline-secondary"
                      :disabled="isRoundAssignmentLocked(round) || !judges.length || isRoundSaving(round.id)"
                      @click="emitSkip(round)"
                    >
                      <i class="fa-solid fa-forward"></i>
                      <span>{{ label('judge.assignment.skip', 'Skip') }}</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import { secureStorage } from '@/services/secureStorage';

export default {
  name: 'CompetitionJudgeAssignmentPanel',
  props: {
    competitionId: {
      type: [Number, String],
      default: null,
    },
    rounds: {
      type: Array,
      default: () => [],
    },
    allRounds: {
      type: Array,
      default: () => [],
    },
    judges: {
      type: Array,
      default: () => [],
    },
    phaseLabel: {
      type: String,
      default: '',
    },
    savingRoundIds: {
      type: Array,
      default: () => [],
    },
    assignmentSettings: {
      type: Object,
      default: () => ({}),
    },
    authToken: {
      type: String,
      default: '',
    },
  },
  emits: ['assign-round-judge', 'apply-auto-assignment', 'skip-round-judge'],
  data() {
    return {
      draftAssignments: {},
      assignmentMode: 'automatic',
      operatorReleaseMode: 'after_uploads',
      backendProposal: {
        queue_judges: [],
        timed_pending_rounds: [],
        preview_assignments: [],
      },
      proposalRefreshTimeoutId: null,
      proposalLoading: false,
      proposalRequestNonce: 0,
      autoQueueOrder: [],
      pendingAssignmentTimers: {},
      autoManagedAssignments: {},
      currentTimestamp: Date.now(),
      autoAssignmentHeartbeatId: null,
      autoDispatchLockId: null,
      autoDispatchInFlight: false,
      autoAssignmentPendingRoundIds: [],
      assignmentSettingsHydrated: false,
      assignmentSettingsSyncing: false,
      assignmentSettingsSaving: false,
    };
  },
  computed: {
    assignmentTimeoutMs() {
      return 60 * 1000;
    },
    autoAssignableStatusIds() {
      return new Set([21, 13, 16, 17, 20]);
    },
    timedAutoAssignmentStatusIds() {
      return new Set([21]);
    },
    busyStatusIds() {
      return new Set([6, 11, 12, 13, 16, 17, 19, 20, 21, 22]);
    },
    videoPendingStatusIds() {
      return new Set([13, 16, 17, 20]);
    },
    phaseLabelText() {
      return this.phaseLabel || this.label('judge.assignment.current_phase', 'Fase actual');
    },
    modeHintText() {
      if (this.assignmentMode === 'manual') {
        return this.label('judge.assignment.mode_manual_hint', 'Trabajas ronda a ronda y el sistema no propone nuevos repartos.');
      }

      return this.label('judge.assignment.mode_automatic_hint', 'El backend gestiona el reparto automatico y esta vista solo refleja el estado actual.');
    },
    phaseHintText() {
      if (!this.rounds.length) {
        return this.label('judge.assignment.phase_hint_empty', 'Selecciona una fase o ronda para trabajar sobre ella.');
      }

      if (this.assignmentMode === 'manual') {
        return this.label('judge.assignment.phase_hint_manual', 'Puedes reasignar manualmente cada ronda o avanzar al siguiente juez con skip.');
      }

      if (this.recalculationStartIndex >= 0) {
        return this.label('judge.assignment.phase_hint_recalc', 'Hay un cambio manual detectado. El backend recalculara la asignacion a partir del nuevo estado.');
      }

      return this.label('judge.assignment.phase_hint_auto', 'Vista de seguimiento del reparto automatico gobernado por backend.');
    },
    queueHintText() {
      if (this.callQueueJudges.length) {
        return this.label('judge.assignment.queue_hint', 'Esta es la cola operativa actual de jueces online, tanto en espera como con ronda activa.');
      }

      return this.label('judge.assignment.queue_hint_empty', 'La cola se vacia cuando todos estan ocupados, offline o retenidos como tecnico.');
    },
    unassignedVisibleRounds() {
      return this.rounds.filter((round) => !this.resolveCurrentJudgeId(round)).length;
    },
    upcomingRounds() {
      const pendingStatuses = new Set([13, 16, 17, 19, 20, 21, 22, 23]);

      return (this.allRounds || [])
        .filter((round) => pendingStatuses.has(Number(round?.status_id)))
        .sort((left, right) => {
          const leftStage = Number(left?.round_size_id || 0);
          const rightStage = Number(right?.round_size_id || 0);
          if (leftStage !== rightStage) {
            return leftStage - rightStage;
          }

          const leftRound = Number(left?.round_number || 0);
          const rightRound = Number(right?.round_number || 0);
          if (leftRound !== rightRound) {
            return leftRound - rightRound;
          }

          return Number(left?.id || 0) - Number(right?.id || 0);
        })
        .slice(0, 6);
    },
    judgeLookupById() {
      return (this.judges || []).reduce((map, judge) => {
        map[String(judge.id)] = judge;
        return map;
      }, {});
    },
    manualOverrideIndices() {
      return (this.rounds || [])
        .map((round, index) => (this.hasDraftChange(round) ? index : -1))
        .filter((index) => index >= 0);
    },
    recalculationStartIndex() {
      if (!this.manualOverrideIndices.length) return -1;
      return Math.min(...this.manualOverrideIndices);
    },
    previewRecalculationRoundIds() {
      if (this.recalculationStartIndex < 0) return [];
      return this.rounds.slice(this.recalculationStartIndex).map((round) => String(round.id));
    },
    phaseHasPendingVideoWorkflow() {
      return (this.rounds || []).some((round) => this.videoPendingStatusIds.has(Number(round?.status_id || 0)));
    },
    currentJudgeStateMap() {
      return this.buildJudgeStateMap({ excludeRoundIds: [] });
    },
    previewJudgeStateMap() {
      return this.buildJudgeStateMap({ excludeRoundIds: this.previewRecalculationRoundIds });
    },
    waitingQueueJudges() {
      return this.callQueueJudges.filter((judge) => judge.state === 'waiting');
    },
    callQueueJudges() {
      return (this.backendProposal?.queue_judges || []).map((judge, index) => ({
        ...judge,
        queueDescription: this.buildBackendQueueJudgeDescription(judge, index),
        queueChipLabel: this.buildBackendQueueJudgeChipLabel(judge, index),
        queueChipClass: this.buildBackendQueueJudgeChipClass(judge, index),
      }));
    },
    timedPendingRounds() {
      const roundsById = (this.rounds || []).reduce((map, round) => {
        map[String(round.id)] = round;
        return map;
      }, {});

      return (this.backendProposal?.timed_pending_rounds || [])
        .map((entry) => {
          const round = roundsById[String(entry?.round_id)];
          if (!round) return null;

          const judgeId = Number(entry?.judge_id || 0);
          const remainingMs = Math.max(0, Number(entry?.remaining_ms || 0));

          return {
            roundId: round.id,
            roundLabel: this.formatRoundLabel(round),
            judgeId,
            judgeLabel: this.resolveJudgeLabelById(judgeId) || this.label('judge.assignment.unassigned_short', 'Sin juez'),
            remainingMs,
            remainingText: this.formatRemainingTime(remainingMs),
          };
        })
        .filter(Boolean)
        .sort((left, right) => left.remainingMs - right.remainingMs);
    },
    occupiedJudges() {
      return Object.values(this.currentJudgeStateMap)
        .filter((judge) => judge.state === 'occupied')
        .sort((left, right) => left.queueOrder - right.queueOrder);
    },
    offlineJudges() {
      return Object.values(this.currentJudgeStateMap)
        .filter((judge) => judge.state === 'offline')
        .sort((left, right) => left.queueOrder - right.queueOrder);
    },
    technicalHeldJudges() {
      return Object.values(this.currentJudgeStateMap)
        .filter((judge) => judge.state === 'technical-held')
        .sort((left, right) => left.queueOrder - right.queueOrder);
    },
    autoCandidateRounds() {
      if (this.assignmentMode !== 'automatic') return [];

      if (this.recalculationStartIndex >= 0) {
        return this.sortAutoCandidateEntries(this.rounds.slice(this.recalculationStartIndex).map((round, offset) => ({
          round,
          index: this.recalculationStartIndex + offset,
        })));
      }

      return this.sortAutoCandidateEntries(this.rounds
        .map((round, index) => ({ round, index }))
        .filter(({ round }) => this.isRoundAutoAssignable(round) && !this.resolveCurrentJudgeId(round)));
    },
    previewAssignments() {
      return this.assignmentMode === 'automatic'
        ? (this.backendProposal?.preview_assignments || []).map((entry) => ({
          roundId: Number(entry?.round_id || 0),
          judgeId: entry?.judge_id === null || entry?.judge_id === undefined ? null : Number(entry.judge_id),
          source: entry?.source || 'automatic',
          explanation: entry?.explanation || '',
          affectsPersistence: Boolean(entry?.affects_persistence),
          requestedJudgeId: entry?.requested_judge_id === null || entry?.requested_judge_id === undefined
            ? null
            : Number(entry.requested_judge_id),
        }))
        : [];
    },
    previewAssignmentsByRoundId() {
      return this.previewAssignments.reduce((map, entry) => {
        map[String(entry.roundId)] = entry;
        return map;
      }, {});
    },
    autoAssignmentsToPersist() {
      return this.previewAssignments
        .filter((entry) => entry.affectsPersistence)
        .filter((entry) => entry.source !== 'blocked');
    },
    hasPreviewChanges() {
      return this.autoAssignmentsToPersist.length > 0;
    },
    hasPreviewConflicts() {
      return this.previewAssignments.some((entry) => entry.source === 'blocked');
    },
    automaticExecutionEnabled() {
      return false;
    },
    previewSummaryTitle() {
      if (this.assignmentMode !== 'automatic') {
        return this.label('judge.assignment.preview_summary_manual', 'Modo manual activo');
      }

      if (this.hasPreviewConflicts) {
        return this.label('judge.assignment.preview_summary_warning', 'La propuesta necesita ajustes');
      }

      if (this.hasPreviewChanges) {
        return this.label('judge.assignment.preview_summary_ready', 'La propuesta esta lista para aplicar');
      }

      return this.label('judge.assignment.preview_summary_idle', 'Sin cambios pendientes');
    },
    previewSummaryText() {
      if (this.assignmentMode !== 'automatic') {
        return this.label('judge.assignment.preview_summary_manual_text', 'Guarda cambios puntuales por ronda mientras defines la estrategia automatica.');
      }

      if (!this.automaticExecutionEnabled) {
        return this.label('judge.assignment.preview_summary_backend_text', 'La propuesta visual es informativa. El backend decide y aplica la asignacion automatica.');
      }

      if (this.hasPreviewConflicts) {
        return this.label('judge.assignment.preview_summary_warning_text', 'Hay jueces elegidos manualmente que ahora mismo estan offline, ocupados o retenidos como tecnico.');
      }

      if (this.hasPreviewChanges) {
        return this.label('judge.assignment.preview_summary_ready_text', 'Puedes aplicar la propuesta para actualizar las rondas afectadas de esta fase.');
      }

      return this.label('judge.assignment.preview_summary_idle_text', 'No hay rondas nuevas que repartir o la cola elegible esta vacia.');
    },
    judgeLoadCards() {
      const totalsByJudge = new Map();

      (this.judges || []).forEach((judge) => {
        totalsByJudge.set(String(judge.id), { ...judge, total: 0, pending: 0 });
      });

      (this.allRounds || []).forEach((round) => {
        const judgeId = Number(round?.judge_id || 0);
        if (!judgeId) return;

        const key = String(judgeId);
        if (!totalsByJudge.has(key)) return;

        const current = totalsByJudge.get(key);
        current.total += 1;
        if (this.busyStatusIds.has(Number(round?.status_id))) {
          current.pending += 1;
        }
      });

      return Array.from(totalsByJudge.values())
        .sort((left, right) => {
          if (left.pending !== right.pending) {
            return right.pending - left.pending;
          }
          if (left.total !== right.total) {
            return right.total - left.total;
          }
          return left.label.localeCompare(right.label);
        })
        .slice(0, 6);
    },
  },
  watch: {
    assignmentSettings: {
      immediate: true,
      deep: true,
      handler(settings) {
        this.hydrateAssignmentSettings(settings, { allowLocalFallback: true });
      },
    },
    competitionId: {
      immediate: true,
      handler() {
        this.hydrateAssignmentSettings(this.assignmentSettings, { allowLocalFallback: true });
        this.scheduleAssignmentProposalRefresh();
      },
    },
    rounds: {
      deep: true,
      immediate: true,
      handler(rounds) {
        const nextDrafts = {};

        (rounds || []).forEach((round) => {
          nextDrafts[String(round.id)] = this.normalizeDraftValue(round?.judge_id);
        });

        this.draftAssignments = nextDrafts;
        this.scheduleAssignmentProposalRefresh();
      },
    },
    judges: {
      deep: true,
      immediate: true,
      handler() {
        this.scheduleAssignmentProposalRefresh();
      },
    },
    assignmentMode() {
      this.persistAssignmentSettings();
      this.saveAssignmentSettings();
      this.scheduleAssignmentProposalRefresh();
    },
    operatorReleaseMode() {
      this.persistAssignmentSettings();
      this.saveAssignmentSettings();
      this.scheduleAssignmentProposalRefresh();
    },
    savingRoundIds: {
      deep: true,
      handler() {
        this.scheduleAssignmentProposalRefresh();
      },
    },
  },
  mounted() {
    this.hydrateAssignmentSettings(this.assignmentSettings, { allowLocalFallback: true });
    this.scheduleAssignmentProposalRefresh();
  },
  beforeUnmount() {
    this.clearAssignmentProposalRefresh();
    this.releaseAutoDispatchLock();
  },
  methods: {
    label(key, fallback) {
      const translated = typeof this.$t === 'function' ? this.$t(key) : '';
      return translated && translated !== key ? translated : fallback;
    },
    normalizeDraftValue(value) {
      return value === null || value === undefined || value === '' ? '' : String(value);
    },
    getAssignmentSettingsStorageKey() {
      const competitionId = String(this.competitionId || '').trim();
      if (!competitionId) return '';
      return `competitionAssignmentSettings:${competitionId}`;
    },
    normalizeAssignmentMode(value) {
      return value === 'manual' ? 'manual' : 'automatic';
    },
    normalizeOperatorReleaseMode(value) {
      return value === 'always' ? 'always' : 'after_uploads';
    },
    restoreLocalAssignmentSettings() {
      const storageKey = this.getAssignmentSettingsStorageKey();
      if (!storageKey) return null;

      try {
        const rawValue = secureStorage.getItem(storageKey);
        if (!rawValue) return null;

        return JSON.parse(rawValue);
      } catch (error) {
        console.warn('[AssignmentPanel] failed to restore persisted settings', error?.message || error);
        return null;
      }
    },
    hydrateAssignmentSettings(settings = {}, options = {}) {
      const { allowLocalFallback = false } = options;
      const localSettings = allowLocalFallback ? this.restoreLocalAssignmentSettings() : null;
      const nextAssignmentMode = this.normalizeAssignmentMode(
        settings?.assignment_mode
          ?? settings?.assignmentMode
          ?? localSettings?.assignmentMode
      );
      const nextReleaseMode = this.normalizeOperatorReleaseMode(
        settings?.tech_operator_release_mode
          ?? settings?.operatorReleaseMode
          ?? localSettings?.operatorReleaseMode
      );

      this.assignmentSettingsSyncing = true;
      this.assignmentMode = nextAssignmentMode;
      this.operatorReleaseMode = nextReleaseMode;
      this.assignmentSettingsHydrated = true;
      this.assignmentSettingsSyncing = false;

      this.persistAssignmentSettings();
    },
    persistAssignmentSettings() {
      const storageKey = this.getAssignmentSettingsStorageKey();
      if (!storageKey) return;

      secureStorage.setItem(storageKey, JSON.stringify({
        assignmentMode: this.assignmentMode,
        operatorReleaseMode: this.operatorReleaseMode,
      }));
    },
    async saveAssignmentSettings() {
      if (!this.assignmentSettingsHydrated || this.assignmentSettingsSyncing || this.assignmentSettingsSaving) {
        return;
      }

      const competitionId = String(this.competitionId || '').trim();
      const token = this.authToken || (secureStorage.getItem('token') || localStorage.getItem('token') || '').replace(/"/g, '');

      if (!competitionId || !token) {
        return;
      }

      this.assignmentSettingsSaving = true;

      try {
        await this.$axios.post(
          `/competition/${competitionId}/assignment-settings`,
          {
            assignment_mode: this.assignmentMode,
            tech_operator_release_mode: this.operatorReleaseMode,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } catch (error) {
        console.warn('[AssignmentPanel] failed to save assignment settings', error?.response?.data?.message || error?.message || error);
      } finally {
        this.assignmentSettingsSaving = false;
      }
    },
    clearAssignmentProposalRefresh() {
      if (!this.proposalRefreshTimeoutId) return;
      clearTimeout(this.proposalRefreshTimeoutId);
      this.proposalRefreshTimeoutId = null;
    },
    scheduleAssignmentProposalRefresh() {
      this.clearAssignmentProposalRefresh();
      this.proposalRefreshTimeoutId = setTimeout(() => {
        this.proposalRefreshTimeoutId = null;
        this.fetchAssignmentProposal();
      }, 120);
    },
    async fetchAssignmentProposal() {
      const competitionId = String(this.competitionId || '').trim();
      const token = this.authToken || (secureStorage.getItem('token') || localStorage.getItem('token') || '').replace(/"/g, '');

      if (!competitionId || !token) {
        this.backendProposal = { queue_judges: [], timed_pending_rounds: [], preview_assignments: [] };
        return;
      }

      const requestNonce = Date.now();
      this.proposalRequestNonce = requestNonce;
      this.proposalLoading = true;

      try {
        const response = await this.$axios.post(
          `/competition/${competitionId}/assignment-proposal`,
          {
            round_ids: (this.rounds || []).map((round) => Number(round?.id || 0)).filter((id) => id > 0),
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (this.proposalRequestNonce !== requestNonce) {
          return;
        }

        this.backendProposal = {
          queue_judges: response?.data?.proposal?.queue_judges || [],
          timed_pending_rounds: response?.data?.proposal?.timed_pending_rounds || [],
          preview_assignments: response?.data?.proposal?.preview_assignments || [],
        };
      } catch (error) {
        if (this.proposalRequestNonce !== requestNonce) {
          return;
        }

        console.warn('[AssignmentPanel] failed to load backend assignment proposal', error?.response?.data?.message || error?.message || error);
        this.backendProposal = { queue_judges: [], timed_pending_rounds: [], preview_assignments: [] };
      } finally {
        if (this.proposalRequestNonce === requestNonce) {
          this.proposalLoading = false;
        }
      }
    },
    isRoundAutoAssignable(round) {
      return this.autoAssignableStatusIds.has(Number(round?.status_id || 0));
    },
    shouldIncludeRoundInAutomaticRebalance(round) {
      if (!this.isRoundAutoAssignable(round)) return false;
      return Number(this.resolveCurrentJudgeId(round) || 0) > 0;
    },
    isTimedPendingRound(round) {
      return Number(this.resolveCurrentJudgeId(round) || 0) > 0 && this.timedAutoAssignmentStatusIds.has(Number(round?.status_id || 0));
    },
    sortAutoCandidateEntries(entries = []) {
      return [...entries].sort((left, right) => Number(left?.index || 0) - Number(right?.index || 0));
    },
    resolveCurrentJudgeId(round) {
      const judgeId = round?.judge_id;
      const normalizedJudgeId = judgeId === null || judgeId === undefined || judgeId === '' ? null : Number(judgeId);
      if (!normalizedJudgeId) {
        return null;
      }

      if (!this.isRoundAutoAssignable(round)) {
        return normalizedJudgeId;
      }

      const judge = this.judgeLookupById[String(normalizedJudgeId)] || null;
      if (!judge?.isOnline) {
        return null;
      }

      const isTechnicalHeld = Boolean(judge?.techOperatorActive)
        && this.operatorReleaseMode === 'after_uploads'
        && this.phaseHasPendingVideoWorkflow;

      return isTechnicalHeld ? null : normalizedJudgeId;
    },
    resolveJudgeLabelById(judgeId) {
      if (!judgeId) return '';
      return this.judgeLookupById[String(judgeId)]?.label || '';
    },
    resolveCurrentJudgeLabel(round) {
      return this.resolveJudgeLabelById(this.resolveCurrentJudgeId(round));
    },
    formatRoundLabel(round) {
      const athleteName = [round?.user?.name, round?.user?.surname].filter(Boolean).join(' ').trim();
      if (athleteName) {
        return athleteName;
      }

      return round?.round_name || `${this.label('rounds.round', 'Ronda')} #${round?.id || '-'}`;
    },
    formatRoundMeta(round) {
      const parts = [];

      if (round?.round_size_id) {
        parts.push(`${this.label('judge.assignment.stage', 'Etapa')} ${round.round_size_id}`);
      } else if (round?.round_number) {
        parts.push(`${this.label('rounds.qualifying', 'Calificacion')} ${round.round_number}`);
      }

      if (round?.vs_group && round?.vs_match_number) {
        parts.push(`VS ${round.vs_group}-${round.vs_match_number}`);
      }

      const statusId = Number(round?.status_id || 0);
      if (statusId) {
        parts.push(`${this.label('judge.assignment.status', 'Estado')} ${statusId}`);
      }

      return parts.join(' · ');
    },
    resolveAssignmentTimerStartedAt(round) {
      const candidates = [
        round?.assignment_timer_started_at,
        round?.judge_assigned_at,
        round?.updated_at,
        round?.created_at,
      ];

      for (const candidate of candidates) {
        const parsed = Date.parse(candidate);
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }

      return Date.now();
    },
    updateDraft(roundId, value) {
      this.draftAssignments = {
        ...this.draftAssignments,
        [String(roundId)]: value,
      };
    },
    hasDraftChange(round) {
      const current = this.normalizeDraftValue(round?.judge_id);
      const draft = this.normalizeDraftValue(this.draftAssignments[String(round?.id)]);
      return current !== draft;
    },
    isRoundSaving(roundId) {
      return (this.savingRoundIds || []).map(String).includes(String(roundId));
    },
    isRoundAssignmentLocked(round) {
      const statusId = Number(round?.status_id || 0);
      return new Set([6, 7, 10, 11, 12, 19, 22]).has(statusId);
    },
    lockedRoundAssignmentHint(round) {
      const statusId = Number(round?.status_id || 0);

      if (new Set([7, 10, 19, 22]).has(statusId)) {
        return this.label('judge.assignment.already_scored_hint', 'La ronda ya fue calificada o cerrada y no permite cambiar la asignacion.');
      }

      return this.label('judge.assignment.already_in_progress_hint', 'La ronda esta en curso o en scoring y no entra en la reasignacion automatica.');
    },
    getAssignmentScopeRounds() {
      return Array.isArray(this.rounds) ? this.rounds : [];
    },
    judgeBusyRounds(judgeId, excludeRoundIds = []) {
      const excluded = new Set((excludeRoundIds || []).map(String));

      return this.getAssignmentScopeRounds().filter((round) => {
        if (excluded.has(String(round?.id))) return false;
        return Number(round?.judge_id || 0) === Number(judgeId) && this.busyStatusIds.has(Number(round?.status_id || 0));
      });
    },
    buildJudgeStateMap({ excludeRoundIds = [] } = {}) {
      return (this.judges || []).reduce((map, judge) => {
        const busyRounds = this.judgeBusyRounds(judge.id, excludeRoundIds);
        const isOnline = Boolean(judge?.isOnline);
        const isTechnicalHeld = Boolean(judge?.techOperatorActive)
          && this.operatorReleaseMode === 'after_uploads'
          && this.phaseHasPendingVideoWorkflow;

        let state = 'waiting';
        let reason = this.label('judge.assignment.state_waiting', 'Listo para la siguiente ronda.');

        if (!isOnline) {
          state = 'offline';
          reason = judge?.lastSeenAt
            ? `${this.label('judge.assignment.state_offline', 'Desconectado')} · ${this.formatLastSeen(judge.lastSeenAt)}`
            : this.label('judge.assignment.state_offline', 'Desconectado');
        } else if (isTechnicalHeld) {
          state = 'technical-held';
          reason = this.label('judge.assignment.state_technical', 'Sigue retenido como operador tecnico hasta cerrar grabacion y upload.');
        } else if (busyRounds.length) {
          state = 'occupied';
          reason = this.describeBusyRounds(busyRounds);
        }

        map[String(judge.id)] = {
          ...judge,
          state,
          reason,
          busyRounds,
          queueOrder: this.getQueuePosition(judge.id, judge?.queueOrder || 0),
        };
        return map;
      }, {});
    },
    getAutoEligibleJudgeQueue(ignoreRoundIds = []) {
      const ignoredRoundIds = new Set((ignoreRoundIds || []).map(String));
      const scopeRounds = this.getAssignmentScopeRounds();

      return (this.judges || [])
        .map((judge) => {
          const busyRounds = scopeRounds.filter((round) => {
            if (ignoredRoundIds.has(String(round?.id))) return false;
            return Number(round?.judge_id || 0) === Number(judge.id) && this.busyStatusIds.has(Number(round?.status_id || 0));
          });

          const isTechnicalHeld = Boolean(judge?.techOperatorActive)
            && this.operatorReleaseMode === 'after_uploads'
            && this.phaseHasPendingVideoWorkflow;

          return {
            ...judge,
            busyRounds,
            isEligible: Boolean(judge?.isOnline) && !isTechnicalHeld && busyRounds.length === 0,
            queueOrder: this.getQueuePosition(judge.id, judge?.queueOrder || 0),
          };
        })
        .filter((judge) => judge.isEligible)
        .sort((left, right) => left.queueOrder - right.queueOrder);
    },
    getQueuePosition(judgeId, fallback = 0) {
      const index = this.autoQueueOrder.findIndex((id) => Number(id) === Number(judgeId));
      return index >= 0 ? index : Number(fallback || 0);
    },
    syncAutoQueueOrder() {
      const judgeIds = (this.judges || [])
        .filter((judge) => Boolean(judge?.isOnline))
        .map((judge) => Number(judge.id))
        .filter((id) => Number.isFinite(id) && id > 0);

      if (!judgeIds.length) {
        this.autoQueueOrder = [];
        return;
      }

      const next = this.autoQueueOrder.filter((id) => judgeIds.includes(Number(id)));
      judgeIds.forEach((id) => {
        if (!next.includes(id)) {
          next.push(id);
        }
      });

      this.autoQueueOrder = next;
    },
    moveJudgesToQueueTail(judgeIds = []) {
      const normalized = [...new Set((judgeIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0))];
      if (!normalized.length) return;

      const remaining = this.autoQueueOrder.filter((id) => !normalized.includes(Number(id)));
      const tail = normalized.filter((id) => this.autoQueueOrder.includes(id));
      const unseen = (this.judges || [])
        .filter((judge) => Boolean(judge?.isOnline))
        .map((judge) => Number(judge.id))
        .filter((id) => Number.isFinite(id) && id > 0 && !remaining.includes(id) && !tail.includes(id));

      this.autoQueueOrder = [...remaining, ...tail, ...unseen];
    },
    moveTimedOutJudgeDown(currentJudgeId) {
      const normalizedCurrent = Number(currentJudgeId || 0);
      if (!normalizedCurrent) return;

      const remaining = this.autoQueueOrder.filter((id) => Number(id) !== normalizedCurrent);
      this.autoQueueOrder = [...remaining, normalizedCurrent];
    },
    cleanupAutomaticState(rounds = this.rounds) {
        const roundsById = (rounds || []).reduce((map, round) => {
          map[String(round?.id)] = round;
          return map;
        }, {});

        this.autoManagedAssignments = Object.entries(this.autoManagedAssignments).reduce((map, [roundId, managed]) => {
          const round = roundsById[String(roundId)];
          if (!round) return map;

          const currentJudgeId = Number(this.resolveCurrentJudgeId(round) || 0);
          if (!currentJudgeId || currentJudgeId !== Number(managed?.judgeId || 0)) return map;

          if (!this.autoAssignableStatusIds.has(Number(round?.status_id || 0))) return map;

          map[String(roundId)] = managed;
          return map;
        }, {});

        this.pendingAssignmentTimers = Object.entries(this.pendingAssignmentTimers).reduce((map, [roundId, timer]) => {
          if (!this.autoManagedAssignments[String(roundId)]) return map;
          map[String(roundId)] = timer;
          return map;
        }, {});
    },
    buildRebalanceAssignments() {
        const candidateEntries = this.sortAutoCandidateEntries((this.rounds || [])
          .map((round, index) => ({ round, index }))
          .filter(({ round }) => {
            if (!this.isRoundAutoAssignable(round)) return false;
            return !this.resolveCurrentJudgeId(round) || this.shouldIncludeRoundInAutomaticRebalance(round);
          }));

        if (!candidateEntries.length) {
          return [];
        }

        const eligibleJudges = this.getAutoEligibleJudgeQueue(candidateEntries.map(({ round }) => round?.id));

        return candidateEntries
          .map(({ round }, index) => {
            const targetJudge = eligibleJudges[index] || null;
            const targetJudgeId = targetJudge ? Number(targetJudge.id) : null;
            const currentJudgeId = this.resolveCurrentJudgeId(round);

            if (this.normalizeDraftValue(currentJudgeId) === this.normalizeDraftValue(targetJudgeId)) {
              return null;
            }

            return {
              roundId: round.id,
              judgeId: targetJudgeId,
            };
          })
          .filter(Boolean);
      },
    syncPendingAssignmentTimers(rounds = this.rounds) {
      const next = {};
      const nextManaged = {};

      (rounds || []).forEach((round) => {
        const roundId = String(round?.id || '');
        const currentJudgeId = Number(this.resolveCurrentJudgeId(round) || 0);
        const hasCurrentJudge = Number.isFinite(currentJudgeId) && currentJudgeId > 0;
        const shouldManageRound = hasCurrentJudge && this.isRoundAutoAssignable(round);
        const managedEntry = shouldManageRound
          ? (this.autoManagedAssignments[roundId] || {
              judgeId: currentJudgeId,
              startedAt: this.resolveAssignmentTimerStartedAt(round),
            })
          : null;

        if (!managedEntry) {
          return;
        }

        const judgeId = currentJudgeId;
        const judge = this.judgeLookupById[String(judgeId)] || null;

        if (Number(managedEntry.judgeId) !== judgeId) {
          nextManaged[roundId] = {
            judgeId,
            startedAt: this.resolveAssignmentTimerStartedAt(round),
          };

          if (this.isTimedPendingRound(round) && judge?.isOnline) {
            next[roundId] = {
              judgeId,
              startedAt: this.resolveAssignmentTimerStartedAt(round),
            };
          }
          return;
        }

        nextManaged[roundId] = managedEntry;

        if (!this.isTimedPendingRound(round)) {
          return;
        }

        if (!judge?.isOnline) {
          return;
        }

        next[roundId] = {
          judgeId,
          startedAt: this.resolveAssignmentTimerStartedAt(round),
        };
      });

      this.pendingAssignmentTimers = next;
      this.autoManagedAssignments = nextManaged;
    },
    formatRemainingTime(remainingMs = 0) {
      const totalSeconds = Math.max(0, Math.ceil(Number(remainingMs || 0) / 1000));
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },
    buildBackendQueueJudgeDescription(judge, index) {
      if (judge?.state === 'occupied') {
        return judge?.reason || this.label('judge.assignment.busy_scoring', 'Tiene una ronda activa en cola operativa.');
      }

      if (index === 0) {
        return this.label('judge.assignment.next_up', 'Siguiente ronda');
      }

      return judge?.reason || this.label('judge.assignment.waiting_round', 'En espera');
    },
    buildBackendQueueJudgeChipLabel(judge, index) {
      if (judge?.state === 'occupied') {
        return this.label('judge.assignment.queue_occupied', 'Asignado');
      }

      if (index === 0) {
        return this.label('judge.assignment.now_serving', 'Siguiente');
      }

      return this.label('judge.assignment.in_queue', 'En cola');
    },
    buildBackendQueueJudgeChipClass(judge, index) {
      if (judge?.state === 'occupied') {
        return 'assignment-chip--busy';
      }

      if (index === 0) {
        return 'assignment-chip--queue';
      }

      return 'assignment-chip--queue';
    },
    collectTimedOutAssignments() {
      if (this.assignmentMode !== 'automatic') return [];

      const onlineOperationalQueue = this.callQueueJudges.map((judge) => Number(judge.id));
      const offlineManagedAssignments = Object.entries(this.autoManagedAssignments)
        .map(([roundId]) => {
          const round = (this.rounds || []).find((entry) => String(entry?.id) === String(roundId));
          if (!round || !this.isRoundAutoAssignable(round)) return null;

          const currentJudgeId = Number(this.resolveCurrentJudgeId(round) || 0);
          const judge = this.judgeLookupById[String(currentJudgeId)] || null;
          if (!currentJudgeId || judge?.isOnline) return null;

          const nextJudgeId = onlineOperationalQueue.find((judgeId) => Number(judgeId) !== currentJudgeId);
          if (!nextJudgeId) return null;

          return {
            roundId: Number(roundId),
            judgeId: nextJudgeId,
            previousJudgeId: currentJudgeId,
          };
        })
        .filter(Boolean);

      if (offlineManagedAssignments.length) {
        return this.buildRebalanceAssignments();
      }

      const timedOutAssignments = this.timedPendingRounds
        .filter((item) => item.remainingMs <= 0)
        .map((item) => {
          const nextJudgeId = onlineOperationalQueue.find((judgeId) => Number(judgeId) !== Number(item.judgeId));
          if (!nextJudgeId) {
            return null;
          }

          return {
            roundId: item.roundId,
            judgeId: nextJudgeId,
            previousJudgeId: item.judgeId,
          };
        })
        .filter(Boolean);

      if (!timedOutAssignments.length) {
        return [];
      }

      timedOutAssignments.forEach((assignment) => {
        this.moveTimedOutJudgeDown(assignment.previousJudgeId, assignment.judgeId);
      });

      return this.buildRebalanceAssignments();
    },
    collectQueueEmptyReleaseAssignments() {
      if (this.assignmentMode !== 'automatic') return [];

      const onlineJudges = (this.judges || []).filter((judge) => Boolean(judge?.isOnline));
      if (onlineJudges.length > 0) {
        return [];
      }

      return (this.rounds || [])
        .filter((round) => {
          const roundId = String(round?.id || '');
          if (!this.isRoundAutoAssignable(round)) return false;
          if (!this.autoManagedAssignments[roundId]) return false;
          return Boolean(this.resolveCurrentJudgeId(round));
        })
        .map((round) => ({
          roundId: round.id,
          judgeId: null,
        }));
    },
    startAutoAssignmentHeartbeat() {
      if (this.autoAssignmentHeartbeatId) {
        return;
      }

      this.autoAssignmentHeartbeatId = setInterval(() => {
        this.currentTimestamp = Date.now();
        this.syncAutoQueueOrder();
        this.syncPendingAssignmentTimers(this.rounds);
      }, 1000);
    },
    stopAutoAssignmentHeartbeat() {
      if (!this.autoAssignmentHeartbeatId) {
        return;
      }

      clearInterval(this.autoAssignmentHeartbeatId);
      this.autoAssignmentHeartbeatId = null;
    },
    releaseAutoDispatchLock() {
      if (this.autoDispatchLockId) {
        clearTimeout(this.autoDispatchLockId);
        this.autoDispatchLockId = null;
      }

      this.autoDispatchInFlight = false;
      this.autoAssignmentPendingRoundIds = [];
    },
    rememberAutoManagedAssignments(assignments = []) {
      if (!Array.isArray(assignments) || !assignments.length) return;

      const next = { ...this.autoManagedAssignments };
      const now = Date.now();

      assignments.forEach((assignment) => {
        const roundId = String(assignment?.roundId || '');
        const hasJudge = assignment?.judgeId !== null && assignment?.judgeId !== undefined && assignment?.judgeId !== '';
        const judgeId = hasJudge ? Number(assignment?.judgeId) : null;
        if (!roundId) return;

        if (!hasJudge || !Number.isFinite(judgeId) || judgeId <= 0) {
          delete next[roundId];
          return;
        }

        next[roundId] = {
          judgeId,
          startedAt: now,
        };
      });

      this.autoManagedAssignments = next;
    },
    lockAutoDispatch(roundIds = []) {
      this.releaseAutoDispatchLock();
      this.autoDispatchInFlight = true;
      this.autoAssignmentPendingRoundIds = [...new Set((roundIds || []).map(String))];
      this.autoDispatchLockId = setTimeout(() => {
        this.releaseAutoDispatchLock();
      }, 3500);
    },
    queueAutomaticCycle() {
      if (!this.automaticExecutionEnabled) {
        return;
      }

      this.syncAutoQueueOrder();

      if (this.autoDispatchInFlight || this.assignmentMode !== 'automatic') {
        return;
      }

      if ((this.savingRoundIds || []).length) {
        return;
      }

      const queueEmptyReleaseAssignments = this.collectQueueEmptyReleaseAssignments();
      if (queueEmptyReleaseAssignments.length) {
        this.rememberAutoManagedAssignments(queueEmptyReleaseAssignments);
        this.lockAutoDispatch(queueEmptyReleaseAssignments.map((assignment) => assignment.roundId));
        this.$emit('apply-auto-assignment', queueEmptyReleaseAssignments.map((assignment) => ({
          roundId: assignment.roundId,
          judgeId: assignment.judgeId,
        })));
        return;
      }

      const timedOutAssignments = this.collectTimedOutAssignments();
      if (timedOutAssignments.length) {
        this.rememberAutoManagedAssignments(timedOutAssignments);
        this.lockAutoDispatch(timedOutAssignments.map((assignment) => assignment.roundId));
        this.$emit('apply-auto-assignment', timedOutAssignments.map((assignment) => ({
          roundId: assignment.roundId,
          judgeId: assignment.judgeId,
        })));
        return;
      }

      if (!this.autoAssignmentsToPersist.length || this.hasPreviewConflicts) {
        return;
      }

      const servedJudgeIds = this.autoAssignmentsToPersist
        .map((entry) => Number(entry.judgeId || 0))
        .filter((id) => Number.isFinite(id) && id > 0);

      this.moveJudgesToQueueTail(servedJudgeIds);
      this.rememberAutoManagedAssignments(this.autoAssignmentsToPersist);
      this.lockAutoDispatch(this.autoAssignmentsToPersist.map((entry) => entry.roundId));
      this.$emit('apply-auto-assignment', this.autoAssignmentsToPersist.map((entry) => ({
        roundId: entry.roundId,
        judgeId: entry.judgeId,
      })));
    },
    describeBusyRounds(rounds) {
      const firstRound = rounds[0];
      if (!firstRound) {
        return this.label('judge.assignment.state_busy', 'Ocupado con una ronda pendiente.');
      }

      const statusId = Number(firstRound?.status_id || 0);
      const statusMap = {
        13: this.label('judge.assignment.busy_pending', 'Tiene una ronda pendiente ya asignada.'),
        16: this.label('judge.assignment.busy_recording', 'Esta grabando una ronda ahora mismo.'),
        17: this.label('judge.assignment.busy_recorded', 'Tiene una ronda grabada pendiente de cerrar.'),
        19: this.label('judge.assignment.busy_review', 'Tiene una ronda abierta en revision.'),
        20: this.label('judge.assignment.busy_uploading', 'Tiene una ronda en subida o cola de proceso.'),
        21: this.label('judge.assignment.busy_uploaded', 'Tiene una ronda subida pendiente de cierre.'),
        22: this.label('judge.assignment.busy_editing', 'Tiene una ronda en scoring o edicion.'),
        6: this.label('judge.assignment.busy_scoring', 'Tiene una ronda en scoring o edicion.'),
      };

      return statusMap[statusId] || this.label('judge.assignment.state_busy', 'Ocupado con una ronda pendiente.');
    },
    formatLastSeen(value) {
      if (!value) return this.label('judge.assignment.last_seen_unknown', 'Ultima conexion desconocida');

      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return this.label('judge.assignment.last_seen_unknown', 'Ultima conexion desconocida');

      return `${this.label('judge.assignment.last_seen', 'Ultima vez')} ${parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    },
    buildPreviewEntry({ round, index, judgeId, source, explanation, requestedJudgeId = null }) {
      const assignedJudgeId = judgeId === null || judgeId === undefined ? null : Number(judgeId);
      const currentJudgeId = this.resolveCurrentJudgeId(round);

      return {
        roundId: round.id,
        index,
        judgeId: assignedJudgeId,
        judgeLabel: assignedJudgeId ? this.resolveJudgeLabelById(assignedJudgeId) : this.label('judge.assignment.unassigned_short', 'Sin juez'),
        currentJudgeId,
        requestedJudgeId,
        source,
        explanation,
        affectsPersistence: this.normalizeDraftValue(currentJudgeId) !== this.normalizeDraftValue(assignedJudgeId),
      };
    },
    previewLabel(entry) {
      if (!entry) {
        return this.label('judge.assignment.preview_keep', 'Sin cambios');
      }

      if (entry.source === 'blocked') {
        const requested = entry.requestedJudgeId ? this.resolveJudgeLabelById(entry.requestedJudgeId) : '';
        return requested || this.label('judge.assignment.preview_blocked', 'Bloqueado');
      }

      return entry.judgeLabel;
    },
    previewDescription(entry) {
      if (!entry) {
        return this.label('judge.assignment.preview_keep_hint', 'Mantiene la asignacion actual.');
      }

      return entry.explanation;
    },
    previewChipClass(entry) {
      if (!entry) return 'assignment-chip--neutral';
      if (entry.source === 'manual') return 'assignment-chip--manual';
      if (entry.source === 'blocked') return 'assignment-chip--warning';
      if (entry.source === 'exhausted') return 'assignment-chip--empty';
      return 'assignment-chip--auto';
    },
    describeQueueJudge(judge, index) {
      if (judge.state === 'occupied') {
        return judge.reason || this.label('judge.assignment.busy_scoring', 'Tiene una ronda activa en cola operativa.');
      }

      if (index === 0) {
        return this.label('judge.assignment.next_up', 'Siguiente ronda');
      }

      return this.label('judge.assignment.waiting_round', 'En espera');
    },
    describeQueueJudgeChip(judge, index) {
      if (judge.state === 'occupied') {
        return this.label('judge.assignment.queue_occupied', 'Asignado');
      }

      if (index === 0) {
        return this.label('judge.assignment.now_serving', 'Siguiente');
      }

      return this.label('judge.assignment.in_queue', 'En cola');
    },
    describeQueueJudgeChipClass(judge, index) {
      if (judge.state === 'occupied') {
        return 'assignment-chip--busy';
      }

      if (index === 0) {
        return 'assignment-chip--queue';
      }

      return 'assignment-chip--queue';
    },
    emitAssign(round) {
      const rawValue = this.draftAssignments[String(round.id)];
      const judgeId = rawValue === '' || rawValue === undefined ? null : Number(rawValue);
      this.$emit('assign-round-judge', {
        roundId: round.id,
        judgeId,
      });
    },
    emitSkip(round) {
      this.$emit('skip-round-judge', {
        roundId: round.id,
      });
    },
    emitApplyAutoAssignment() {
      if (!this.automaticExecutionEnabled) return;
      if (!this.autoAssignmentsToPersist.length || this.hasPreviewConflicts) return;

      const servedJudgeIds = this.autoAssignmentsToPersist
        .map((entry) => Number(entry.judgeId || 0))
        .filter((id) => Number.isFinite(id) && id > 0);
      this.moveJudgesToQueueTail(servedJudgeIds);
      this.rememberAutoManagedAssignments(this.autoAssignmentsToPersist);
      this.lockAutoDispatch(this.autoAssignmentsToPersist.map((entry) => entry.roundId));

      this.$emit('apply-auto-assignment', this.autoAssignmentsToPersist.map((entry) => ({
        roundId: entry.roundId,
        judgeId: entry.judgeId,
      })));
    },
  },
};
</script>

<style scoped>
.assignment-panel {
  display: grid;
  gap: 1.25rem;
}

.assignment-panel__header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  flex-wrap: wrap;
}

.assignment-panel__eyebrow {
  margin: 0 0 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.72rem;
  color: #6b7280;
}

.assignment-panel__title {
  margin: 0;
}

.assignment-panel__subtitle {
  margin: 0.35rem 0 0;
  max-width: 54rem;
  color: #4b5563;
}

.assignment-panel__stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
  flex: 1;
}

.assignment-stat-card,
.assignment-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
}

.assignment-stat-card {
  padding: 0.9rem 1rem;
}

.assignment-stat-card__label {
  display: block;
  font-size: 0.8rem;
  color: #6b7280;
}

.assignment-stat-card__value {
  display: block;
  margin-top: 0.35rem;
  font-size: 1.4rem;
}

.assignment-panel__body {
  display: grid;
  grid-template-columns: minmax(300px, 360px) minmax(0, 1fr);
  gap: 1rem;
}

.assignment-panel__sidebar {
  display: grid;
  gap: 1rem;
  align-content: start;
}

.assignment-card {
  padding: 1rem;
}

.assignment-card--main {
  min-width: 0;
}

.assignment-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
}

.assignment-card__header h5,
.assignment-card__header p {
  margin: 0;
}

.assignment-card__header p {
  margin-top: 0.3rem;
  color: #6b7280;
}

.assignment-mode-toggle {
  display: inline-flex;
  gap: 0.5rem;
  margin-bottom: 0.9rem;
}

.assignment-control-grid {
  display: grid;
  gap: 0.85rem;
}

.assignment-field {
  display: grid;
  gap: 0.35rem;
}

.assignment-field span {
  font-size: 0.8rem;
  font-weight: 600;
  color: #475569;
}

.assignment-preview-summary,
.assignment-preview-banner {
  padding: 0.9rem 1rem;
  border-radius: 14px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
}

.assignment-preview-summary strong,
.assignment-preview-banner strong {
  display: block;
}

.assignment-preview-summary p,
.assignment-preview-banner p {
  margin: 0.35rem 0 0;
  color: #475569;
}

.assignment-preview-summary--warning,
.assignment-preview-banner--warning {
  background: #fff7ed;
  border-color: #fdba74;
}

.assignment-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.assignment-actions--stacked {
  margin-top: 0.9rem;
}

.assignment-actions .btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.assignment-countdown-list,
.assignment-callcenter-list,
.assignment-queue,
.assignment-load-list,
.assignment-status-groups {
  display: grid;
  gap: 0.75rem;
}

.assignment-countdown-item,
.assignment-callcenter-item,
.assignment-queue__item,
.assignment-load-item,
.assignment-status-item {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: center;
  padding: 0.8rem 0.9rem;
  border-radius: 12px;
  background: #f8fafc;
}

.assignment-countdown-item {
  background: linear-gradient(135deg, #fff7ed, #fffbeb);
  border: 1px solid #fed7aa;
}

.assignment-callcenter-item__position {
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex: 0 0 auto;
}

.assignment-countdown-item__main,
.assignment-callcenter-item__main,
.assignment-status-item {
  min-width: 0;
  flex: 1;
}

.assignment-countdown-item__main p,
.assignment-countdown-item__main small,
.assignment-callcenter-item__main p,
.assignment-queue__item p,
.assignment-load-item p,
.assignment-status-item p {
  margin: 0.2rem 0 0;
  font-size: 0.82rem;
  color: #6b7280;
}

.assignment-countdown-item__main p {
  color: #9a3412;
}

.assignment-countdown-item__main small {
  color: #b45309;
}

.assignment-status-group {
  display: grid;
  gap: 0.55rem;
}

.assignment-status-group__title {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  font-weight: 700;
}

.assignment-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 0.25rem 0.7rem;
  border-radius: 999px;
  background: #e0f2fe;
  color: #075985;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
}

.assignment-chip--empty {
  background: #f3f4f6;
  color: #6b7280;
}

.assignment-chip--queue {
  background: #dcfce7;
  color: #166534;
}

.assignment-chip--busy {
  background: #ffedd5;
  color: #c2410c;
}

.assignment-chip--timer {
  background: #fff;
  color: #b45309;
  border: 1px solid #fdba74;
  min-width: 4.5rem;
}

.assignment-chip--manual {
  background: #ede9fe;
  color: #5b21b6;
}

.assignment-chip--warning {
  background: #ffedd5;
  color: #c2410c;
}

.assignment-chip--auto {
  background: #dbeafe;
  color: #1d4ed8;
}

.assignment-chip--neutral {
  background: #f8fafc;
  color: #64748b;
}

.assignment-load-item__count {
  font-size: 1.15rem;
  font-weight: 700;
}

.assignment-empty {
  margin: 0;
  color: #6b7280;
}

.assignment-empty--large {
  padding: 1.5rem 0.25rem;
}

.assignment-table-wrap {
  overflow-x: auto;
}

.assignment-table {
  width: 100%;
  border-collapse: collapse;
}

.assignment-table th,
.assignment-table td {
  padding: 0.85rem 0.7rem;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
}

.assignment-table th {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
}

.assignment-row--affected {
  background: rgba(219, 234, 254, 0.32);
}

.assignment-round-cell strong {
  display: block;
}

.assignment-round-cell p {
  margin: 0.25rem 0 0;
  color: #6b7280;
  font-size: 0.82rem;
}

.assignment-preview-cell {
  display: grid;
  gap: 0.35rem;
}

.assignment-preview-cell p {
  margin: 0;
  color: #6b7280;
  font-size: 0.82rem;
}

@media (max-width: 991px) {
  .assignment-panel__body {
    grid-template-columns: 1fr;
  }

  .assignment-panel__stats {
    width: 100%;
  }
}
</style>